import React, { useEffect } from 'react';
import type { DashboardData, Period } from '../../dashboard/types';
import { TRAIN_ELVA_BADGE, reportIdentities } from '../../dashboard/identities';
import { DashHeader, DashTilesRow } from './DashHeader';
import { DispositionsCard, ReasonsCard } from './DashCharts';
import { ScheduleCard } from './DashSchedule';
import { AgentsCard } from './DashAgents';
import { RestraintCard, ReviewQueueCard, TwoRulesCard } from './DashQueues';

export interface DashboardViewProps {
  data: DashboardData;
  onPeriodChange: (next: Period) => void;
  /** False for a `?plan=core` practice (spec 42 §3): the schedule card shows
   * rings and hours only, no gap counts or chips. */
  hasGapFiller?: boolean;
}

/**
 * The Dashboard (spec 17). **The first thing a practice opens** — Settings is
 * finished, and this is the surface that shows whether any of it is working.
 *
 * `Dashboard24h` and `Dashboard30d` are **one component in two data states**, not
 * two pages, which is why the period is a prop and nothing below branches on it
 * except the data it is handed.
 *
 * **Everything here is read-only. Nothing on this page writes.**
 */
export function DashboardView({ data, onPeriodChange, hasGapFiller = true }: DashboardViewProps) {
  // §7: the eight identities are asserted on every render. A failure is a bug to
  // log, not a rounding difference to absorb — a dashboard whose tiles disagree
  // with its own tables teaches people to stop believing all of it.
  useEffect(() => {
    reportIdentities(data);
  }, [data]);

  const appointmentsTotal = data.tiles.appointments.state === 'ok' ? data.tiles.appointments.value : 0;
  const productionCents = data.tiles.productionCents.state === 'ok' ? data.tiles.productionCents.value : 0;

  // The app shell mounts `main` with `fill`, so it is `overflow-hidden` and every
  // page owns its own scroll — the same `min-h-0 flex-1 overflow-y-auto` the
  // Collections page and the settings layout carry. Without it this page renders
  // at full height inside a clipped parent and simply cannot be scrolled.
  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[1500px] flex-1 overflow-y-auto px-8 pb-16 pt-[30px]">
      <DashHeader
        period={data.period}
        asOf={data.asOf}
        windowLabel={data.windowLabel}
        onPeriodChange={onPeriodChange}
      />

      <div className="flex flex-col gap-[22px]">
        <DashTilesRow tiles={data.tiles} period={data.period} />

        {/* Scoped to the period, both of them. */}
        <div className="grid grid-cols-2 items-start gap-[22px] max-[1100px]:grid-cols-1">
          <DispositionsCard counts={data.dispositions} period={data.period} />
          <ReasonsCard
            rows={data.reasons}
            appointmentsTotal={appointmentsTotal}
            productionCents={productionCents}
            period={data.period}
          />
        </div>

        {/* Always now. */}
        <ScheduleCard schedule={data.schedule} hasGapFiller={hasGapFiller} />

        {/* Figures scoped; the attention column is not. */}
        <AgentsCard agents={data.agents} />

        <div className="grid grid-cols-2 items-start gap-[22px] max-[1100px]:grid-cols-1">
          <ReviewQueueCard rows={data.review} badge={TRAIN_ELVA_BADGE} />
          <RestraintCard rows={data.restraint} period={data.period} />
        </div>

        <TwoRulesCard />
      </div>
    </div>
  );
}
